import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, CheckCircle2, ShieldCheck } from "lucide-react";
import { SectionKicker, SectionHeading, FadeIn } from "@/components/shared/SectionBits";

const HERO_IMG =
    "https://customer-assets-jai6qajn.emergentagent.net/job_dynamic-protect-dev/artifacts/ebfx7wsj_ChatGPT%20Image%20Sep%2021%2C%202026%20at%2004_16_29%20AM.png";

const PRICING = [
    {
        product: "ProtectReady download",
        detail: "Single-site software licence",
        price: "£995",
        note: "one-off",
    },
    {
        product: "On-site consultancy",
        detail: "One consultant, up to eight hours",
        price: "£750",
        note: "per day",
    },
    {
        product: "Standard Readiness Package",
        detail: "Software plus two consultancy days",
        price: "From £2,495",
        note: "",
    },
    {
        product: "Enhanced Readiness Package",
        detail: "Software plus three consultancy days",
        price: "From £3,245",
        note: "",
    },
];

const FEATURES = [
    "Structured preparedness assessments aligned to the Act",
    "Organisational responsibility chart with named owners",
    "Training-evidence records and expiry tracking",
    "Practical scenario-testing logs with observations and outcomes",
    "Date- and time-stamped reports and version history",
    "Evidence packs and draft readiness letters for management review",
];

export default function ProtectReadyPage() {
    return (
        <div data-testid="protectready-page">
            {/* Hero */}
            <section className="relative min-h-[65vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="Assessor reviewing readiness on-site at a qualifying venue" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-dr-navy/95 via-dr-navy/80 to-dr-navy/30" />
                </div>
                <div className="relative dr-container w-full py-24">
                    <Link
                        to="/services/security-consultancy/martyns-law"
                        data-testid="back-to-martyns-law"
                        className="inline-flex items-center gap-2 text-white/80 text-sm hover:text-white"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to Martyn's Law
                    </Link>
                    <div className="flex items-center gap-4 mt-8">
                        <span className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-dr-blue text-dr-navy">
                            <ShieldCheck className="w-6 h-6" strokeWidth={2.2} />
                        </span>
                        <p className="text-xs uppercase tracking-[0.32em] text-dr-blue font-medium">
                            ProtectReady · Software &amp; Consultancy
                        </p>
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                        className="font-display font-bold text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] mt-6 max-w-4xl"
                    >
                        Martyn's Law: prepare your organisation with ProtectReady.
                    </motion.h1>
                </div>
            </section>

            {/* Intro */}
            <section className="dr-container py-24 grid lg:grid-cols-12 gap-14">
                <div className="lg:col-span-7">
                    <SectionKicker>About Martyn's Law</SectionKicker>
                    <p className="text-lg text-dr-dark/80 leading-relaxed mt-4">
                        Martyn's Law, formally the Terrorism (Protection of Premises) Act 2025, is designed to
                        improve protection against terrorism at qualifying premises and events. Named in memory of
                        Martyn Hett, who died in the Manchester Arena attack, it introduces duties to prepare
                        effective public protection procedures, with additional requirements for enhanced-tier
                        premises and qualifying events. The Act received Royal Assent in April 2025, with an
                        implementation period before its duties take effect.
                    </p>
                </div>
                <div className="lg:col-span-5">
                    <div className="rounded-2xl bg-dr-grey border border-dr-silver p-8">
                        <p className="text-xs uppercase tracking-[0.28em] text-dr-blue font-medium">At a glance</p>
                        <ul className="mt-6 space-y-3">
                            {[
                                "Prepare compliant public protection procedures",
                                "Document responsibilities and training",
                                "Record practical scenario tests and outcomes",
                                "Run from within your own network, on your terms",
                            ].map((line) => (
                                <li key={line} className="flex gap-3 text-dr-dark/85 text-[15px] leading-relaxed">
                                    <CheckCircle2 className="w-5 h-5 text-dr-navy flex-shrink-0 mt-0.5" />
                                    {line}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ProtectReady product */}
            <section className="bg-dr-grey py-24">
                <div className="dr-container grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-5">
                        <SectionKicker>ProtectReady</SectionKicker>
                        <SectionHeading>Built for your network, and for your evidence trail.</SectionHeading>
                    </div>
                    <div className="lg:col-span-7">
                        <p className="text-lg text-dr-dark/80 leading-relaxed">
                            Dynamic Risks Group has developed <strong>ProtectReady</strong> to help organisations
                            assess their preparedness, document responsibilities, maintain training records and
                            record practical scenario testing.
                        </p>
                        <p className="text-lg text-dr-dark/80 leading-relaxed mt-5">
                            The downloadable application is designed to run within your own corporate or business
                            network, keeping operational records under your organisation's control. It brings
                            together structured assessments, an organisational responsibility chart, training
                            evidence, action tracking and scenario-testing records.
                        </p>
                        <p className="text-lg text-dr-dark/80 leading-relaxed mt-5">
                            Date- and time-stamped reports provide a documented breakdown of findings, while
                            version history records changes to responsibilities and arrangements. Evidence packs
                            and draft readiness letters support management review and preparation for appropriate
                            regulatory correspondence.
                        </p>

                        <div className="grid sm:grid-cols-2 gap-4 mt-10">
                            {FEATURES.map((f, i) => (
                                <FadeIn key={f} delay={i * 0.05}>
                                    <div className="flex gap-3 bg-white border border-dr-silver rounded-xl p-5">
                                        <CheckCircle2 className="w-5 h-5 text-dr-navy flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-dr-dark/85 leading-relaxed">{f}</p>
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="dr-container py-24">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
                    <div className="md:col-span-7">
                        <SectionKicker>Pricing</SectionKicker>
                        <SectionHeading>Proposed software and consultancy pricing.</SectionHeading>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-dr-dark/75 text-lg leading-relaxed">
                            Clearly itemised. Scoped before booking. No hidden fees.
                        </p>
                    </div>
                </div>

                <div className="rounded-2xl border border-dr-silver overflow-hidden bg-white">
                    <div className="hidden md:grid grid-cols-12 bg-dr-navy text-white text-xs uppercase tracking-[0.24em] font-medium">
                        <div className="col-span-8 px-8 py-5">Product or service</div>
                        <div className="col-span-4 px-8 py-5 text-right">Proposed price</div>
                    </div>
                    {PRICING.map((row, i) => (
                        <div
                            key={row.product}
                            data-testid={`pricing-row-${i}`}
                            className={`grid md:grid-cols-12 items-center ${i > 0 ? "border-t border-dr-silver" : ""}`}
                        >
                            <div className="md:col-span-8 px-8 py-6">
                                <p className="font-display text-dr-navy text-lg font-semibold">{row.product}</p>
                                <p className="text-dr-dark/70 text-sm mt-1 leading-relaxed">{row.detail}</p>
                            </div>
                            <div className="md:col-span-4 px-8 pb-6 md:pb-0 md:py-6 md:text-right">
                                <p className="font-display text-dr-navy text-2xl font-bold">{row.price}</p>
                                {row.note && (
                                    <p className="text-xs uppercase tracking-[0.2em] text-dr-dark/60 mt-1">{row.note}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <p className="text-xs text-dr-dark/60 mt-6 leading-relaxed max-w-3xl">
                    All prices are proposed, subject to final confirmation, and exclude VAT where applicable. Travel,
                    accommodation, complex installation, customer hosting and additional work are quoted separately.
                    Installation requires a supported customer-managed IT environment.
                </p>
            </section>

            {/* Practical support */}
            <section className="bg-dr-grey py-24">
                <div className="dr-container grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-5">
                        <SectionKicker>On-site consultancy</SectionKicker>
                        <SectionHeading>Practical support at your premises.</SectionHeading>
                    </div>
                    <div className="lg:col-span-7">
                        <p className="text-lg text-dr-dark/80 leading-relaxed">
                            Our consultancy helps you apply the programme to your organisation. Work can include a
                            site assessment, review of existing procedures, responsibility mapping, training-evidence
                            checks and planned scenario testing.
                        </p>
                        <p className="text-lg text-dr-dark/80 leading-relaxed mt-5">
                            Dynamic Risks consultants help your team exercise its arrangements, record observations
                            and identify improvements. Findings and agreed actions are documented within
                            ProtectReady, providing a clear record for management review.
                        </p>
                        <p className="text-lg text-dr-dark/80 leading-relaxed mt-5">
                            Each package is scoped before booking. Consultancy allocations include preparation and
                            reporting, with the agreed schedule specifying time on site. Larger or more complex
                            premises may require additional days.
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="dr-container py-24">
                <div className="rounded-3xl bg-dr-navy p-10 md:p-16 dr-grain overflow-hidden relative">
                    <div className="grid md:grid-cols-12 gap-10 items-center relative">
                        <div className="md:col-span-8">
                            <p className="text-xs tracking-[0.32em] uppercase text-dr-blue font-medium">Discuss your requirements</p>
                            <h3 className="font-display text-white text-3xl md:text-5xl font-bold leading-tight mt-4">
                                Contact Dynamic Risks Group to scope the ProtectReady package suited to your organisation.
                            </h3>
                            <p className="text-white/70 mt-5 max-w-2xl leading-relaxed">
                                We'll discuss your premises, your existing arrangements and the right combination of
                                software and consultancy days.
                            </p>
                        </div>
                        <div className="md:col-span-4 md:text-right">
                            <Link
                                to="/contact"
                                data-testid="protectready-cta-contact"
                                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-dr-blue text-dr-navy font-semibold hover:bg-white transition-colors"
                            >
                                Speak to a consultant
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>

                <p className="text-xs text-dr-dark/60 mt-8 leading-relaxed max-w-3xl">
                    ProtectReady and our consultancy support organisational readiness. Purchasing the software or
                    completing a package does not constitute certification of legal compliance or SIA approval.
                </p>
            </section>
        </div>
    );
}
